import { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import PreviewThumbs from './PreviewThumbs';
import {
  HoverTimeDetector,
  ProgressBufferRange,
  ProgressHoverDetector,
  ProgressInputRange, ProgressRelativeWrapper,
  ProgressTrack,
} from '../../Player/styles';
import { VideoFileData } from '../../Player/PlayerModels';
import UsePlayerProps from '../../../Store/usePlayerProps';
import { formatSecondsForReminder } from '../../Player/PlayerServices';

const ControlBarProgress = ({ player, playingAd }:
  {
    player: HTMLVideoElement;
    playingAd: boolean,
  }) => {
  const playerGlobalState = UsePlayerProps((state) => state.playerState);
  const { vtt } = playerGlobalState.contentConfigs as VideoFileData;
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [bufferPercent, setBufferPercent] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [hoverPosition, setHoverPosition] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const seekTimer = useRef<any>();
  let seekBar: HTMLElement | null = null;
  const IS_IOS = /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent);

  const calcSliderPos = (e): number => {
    return Number((e.offsetX / e.target.clientWidth) * parseInt(e.target.getAttribute('max'), 10));
  };

  const calcSliderPosOnTouch = (e): number => {
    if (e.touches && e.touches.length > 0) {
      const touch = e.touches[0];
      const rect = e.target.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      return Number((x / rect.width) * parseInt(e.target.getAttribute('max'), 10));
    }
    return 0;
  };

  useEffect(() => {
    player.addEventListener('timeupdate', handleTimeUpdate);
    player.addEventListener('progress', updateBufferPercent);
    player.addEventListener('seeking', handleSeeking);
    player.addEventListener('seeked', handleSeekEnd);

    seekBar = document.getElementById('seekBar');
    seekBar && seekBar.addEventListener('mousemove', handleMouseMove);
    seekBar && seekBar.addEventListener('mouseleave', handleMouseLeave);
    if (trackRef.current) {
      // Add touch event listeners to the div element
      trackRef.current.addEventListener('touchstart', handleTouchStart);
      trackRef.current.addEventListener('touchend', handleTouchEnd);
      trackRef.current.addEventListener('touchmove', handleTouchMove);
    }

    return () => {
      player.removeEventListener('timeupdate', handleTimeUpdate);
      player.removeEventListener('progress', updateBufferPercent);
      player.removeEventListener('seeking', handleSeeking);
      player.removeEventListener('seeked', handleSeekEnd);
      seekBar && seekBar.removeEventListener('mousemove', handleMouseMove);
      seekBar && seekBar.removeEventListener('mouseleave', handleMouseLeave);
      if (trackRef !== null) {
        trackRef?.current?.removeEventListener('touchstart', handleTouchStart);
        trackRef?.current?.removeEventListener('touchend', handleTouchEnd);
        trackRef?.current?.removeEventListener('touchmove', handleTouchMove);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player]);

  const handleMouseMove = (e) => {
    if (!isHovered) {
      setIsHovered(true);
    }
    setHoverPosition(Number(calcSliderPos(e).toFixed(6)));
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setHoverPosition(0);
  };

  const handleTimeUpdate = () => {
    if (!isSeeking) {
      setCurrentTime(player.currentTime);
      player.duration && setDuration(player.duration);
    }
  };

  const updateBufferPercent = () => {
    handleSeekEnd();
    const buffered = player.buffered;
    const duration = player.duration;

    if (buffered.length > 0) {
      const bufferedEnd = buffered.end(buffered.length - 1);
      setBufferPercent((bufferedEnd / duration) * 100);
    }
  };

  const handleSeek = (val) => {
    if (seekTimer.current) clearTimeout(seekTimer.current);
    IS_IOS && setIsHovered(false);
    seekTimer.current = setTimeout(() => {
      player.currentTime = parseFloat(val);
      setCurrentTime(parseFloat(val));
    }, 200);
  };

  const handleSeeking = () => {
    setIsSeeking(true);
  };

  const handleSeekEnd = () => {
    setIsSeeking(false);
  };

  const calcLeftPosition = (): number => {
    return (hoverPosition / duration) * 100;
  };

  const handleTouchStart = (e) => {
    if (trackRef.current) {
      setIsHovered(true);
      setHoverPosition(Number(calcSliderPosOnTouch(e).toFixed(6)));
      !IS_IOS && trackRef.current.style.setProperty('height', '0.75rem');
    }
  };
  const handleTouchMove = (e) => {
    if (trackRef.current) {
      setHoverPosition(Number(calcSliderPosOnTouch(e).toFixed(6)));
      !IS_IOS && trackRef.current.style.setProperty('height', '0.75rem');
    }
  };
  const handleTouchEnd = () => {
    if (trackRef.current) {
      setIsHovered(false);
      !IS_IOS && trackRef.current.style.setProperty('height', '0.5rem');
    }
  };

  return (
    <ProgressRelativeWrapper>
      <ProgressTrack id="progress-track" ref={trackRef}>
        <ProgressInputRange
          id="seekBar"
          disabled={playingAd}
          playingAd={playingAd}
          type="range"
          step="any"
          max={duration}
          value={currentTime}
          onChange={(e) => handleSeek(e.target.value)}
          onTouchEnd={() => handleSeek(hoverPosition)}
        />
        <Progress
          currentTime={currentTime}
          duration={duration}
          playingAd={playingAd}
        />
        <ProgressBufferRange bufferPercent={bufferPercent} />

      </ProgressTrack>
      {isHovered && !playingAd && !!duration && hoverPosition > 0 && (
        <>
          <ProgressHoverDetector left={calcLeftPosition()} playingAd={playingAd} />
          {vtt && playerGlobalState.vttData ? (
            <PreviewThumbs
              show={true}
              currentTime={hoverPosition || currentTime}
              duration={duration}
              vttUrl={vtt}
              parsedVtt={playerGlobalState.vttData}
            />
          ) : <HoverTimeDetector left={calcLeftPosition()}>{formatSecondsForReminder(hoverPosition)}</HoverTimeDetector>}
        </>
      )}
    </ProgressRelativeWrapper>
  );
};

const Progress = styled.div<{ currentTime: number; duration: number; playingAd: boolean; }>(
  ({ theme, currentTime, duration, playingAd }) => {
    return {
      borderRadius: '0.5rem',
      zIndex: 20,
      position: 'absolute',
      width: `calc(${(currentTime / duration) * 100}% + 0px)`,
      maxWidth: '100%',
      height: '100%',
      top: 0,
      left: 0,
      backgroundColor: playingAd ? theme.palette.success[900][0] : theme.palette.primary[800][0],
      pointerEvents: 'none',
    };
  },
);

export default ControlBarProgress;
